/**
 * A preference kept on this device, as a hook.
 *
 * Like the view memory, this is for the way a gardener likes the app to
 * look on the phone in their hand, not something the account carries from
 * device to device — so it lives in localStorage and asks nothing of the
 * server. Every component reading the same key sees a change at once.
 *
 * Who uses it: features/plant-list PlantCard and the profile's
 * ProfilePreferences (how a card shows its photos, CARD_PHOTOS_KEY).
 *
 * @module localPref
 */

import { useCallback, useSyncExternalStore } from 'react';
import { CARD_PHOTOS_STORAGE_KEY, LOCAL_PREF_EVENT } from '@/config/tunables/shared';

/** Fired on this window when a preference is set: 'storage' only reaches other tabs. */
const EVENT = LOCAL_PREF_EVENT;

/**
 * A preference as stored, if it is one of the allowed values.
 *
 * @param key - localStorage key.
 * @param fallback - What it is when unset, not allowed, or storage is refused.
 * @param allowed - The values that count.
 * @returns The stored value, or the fallback.
 */
function read<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const value = window.localStorage.getItem(key);
    return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Keeps a component told of changes: another tab's ('storage') and this
 * one's (EVENT).
 *
 * @param callback - Called on any change; useSyncExternalStore re-reads.
 * @returns What stops it.
 */
function subscribe(callback: () => void): () => void {
  window.addEventListener('storage', callback);
  window.addEventListener(EVENT, callback);
  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener(EVENT, callback);
  };
}

/**
 * A device preference as [value, set]: every component reading the same key
 * redraws when any of them sets it.
 *
 * @param key - localStorage key, e.g. 'bloom.cardPhotos'.
 * @param fallback - What it is until chosen, and if storage is unavailable.
 * @param allowed - The values that count; anything else reads as the fallback.
 * @returns The value now, and a setter that stores it and tells every reader.
 */
export function useLocalPref<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
): [T, (next: T) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => read(key, fallback, allowed),
    () => fallback,
  );
  /** Stores a new value and tells every reader, in this tab (EVENT) and others ('storage'). */
  const set = useCallback((next: T) => {
    try { window.localStorage.setItem(key, next); } catch { /* private mode; it just will not stick */ }
    window.dispatchEvent(new Event(EVENT));
  }, [key]);
  return [value, set];
}

/**
 * How a plant card shows its photos: 'peek' is the Play Store's carousel,
 * the next picture showing at the edge; 'full' is one whole picture at a
 * time. Both swipe.
 */
export const CARD_PHOTOS_KEY = CARD_PHOTOS_STORAGE_KEY;
/** The ways a card can show its photos. */
export const CARD_PHOTOS_VALUES = ['peek', 'full'] as const;
/** One of CARD_PHOTOS_VALUES. */
export type CardPhotos = (typeof CARD_PHOTOS_VALUES)[number];

/*
 * There was a second key here for whether shared frames peek. It is gone,
 * because "how do I like my photographs shown" is one question and was
 * being asked twice. (The share dialog no longer reads either: its peek is
 * a layout chosen in the dialog.)
 */
